"use client";

import { DEFAULT_PRIMARY_RGB, hexToRgb } from "@/lib/color/hex";
import { useEnterpriseTheme } from "@/lib/enterprise/EnterpriseProvider";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import React, { useEffect, useId, useRef, useState } from "react";
import { TEXT_BUTTON_CLASS, TEXT_BUTTON_COLORS } from "./_logic/button.constants";
import { FetchTooltip } from "./FetchTooltip";

/** Cached once per process — the device's pointer capabilities don't change at runtime. */
const SUPPORTS_HOVER =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

/**
 * Whether a link points inside this app and opens in the same tab, so `next/link` can navigate it
 * without a full page load. `//host/path` is protocol-relative, so it is external.
 *
 * @param href - The link target.
 * @param target - The anchor target, if any.
 * @param download - Whether the link downloads a file.
 * @returns True for a same-tab, non-download path starting with a single `/`.
 * @throws Never.
 */
function isInAppPath(href: string, target: string | undefined, download: boolean): boolean {
  if (download || (target && target !== "_self")) return false;
  return href.startsWith("/") && !href.startsWith("//");
}

interface FetchButtonProps {
  children?: React.ReactNode;
  /**
   * `text` is the tertiary action: colored text and icon, heavier weight, no background, no
   * outline, no underline. Hover shifts the text color only.
   */
  variant?: "primary" | "secondary" | "outline" | "ghost" | "destructive" | "text";
  color?: "primary" | "neutral" | "destructive" | "success" | "warning";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  padding?: "xs" | "sm" | "md" | "lg" | "xl";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconOnly?: boolean;
  arrow?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  /**
   * When set, the button renders as a link instead of a `<button>`, keeping every style identical:
   * `next/link` for an in-app path (client-side navigation), a plain `<a>` for anything else. Use
   * for navigation / "open in new tab" affordances so they match the button set exactly instead of
   * being hand-rolled. Pairs with {@link target} / {@link rel}.
   */
  href?: string;
  /**
   * Marks {@link href} as a file download (the anchor's `download` attribute). Set it for a route
   * handler that serves a file: it keeps the plain `<a>`, so `next/link` never prefetches or
   * client-navigates to it.
   */
  download?: boolean;
  /** Anchor target (only used with {@link href}), e.g. "_blank". */
  target?: string;
  /** Anchor rel (only used with {@link href}); defaults to "noopener noreferrer" when target is "_blank". */
  rel?: string;
  tooltip?: React.ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  tooltipAlign?: "start" | "center" | "end";
  /** Optional accessible name override (icon-only or ambiguous-label cases). Visible text remains the primary label. */
  "aria-label"?: string;
  /** Optional id of an element describing this button (e.g. a hint paragraph next to a disabled CTA). */
  "aria-describedby"?: string;
  /** Toggle-button selection state. When set, forwarded as `aria-pressed` so screen readers announce selection. */
  ariaPressed?: boolean;
  /** Disclosure state for a button that shows or hides a panel. Forwarded as `aria-expanded`. */
  ariaExpanded?: boolean;
  /** Id of the panel this button shows or hides. Forwarded as `aria-controls`. */
  ariaControls?: string;
  /** Forwarded as `tabIndex`; -1 takes the button out of the tab order. */
  tabIndex?: number;
  /**
   * Unavailable, but still reachable. Renders `aria-disabled` and the disabled styling while
   * leaving the button in the tab order, and swallows the click.
   *
   * Use instead of {@link disabled} when the reason is not visible at the point of focus — a real
   * `disabled` button is removed from the tab order entirely, so a keyboard user tabbing through
   * a list finds the primary action simply missing, card after card, with no explanation
   * available. Pair it with an `aria-label` (or `aria-describedby`) that says why.
   *
   * Forces `type="button"` while set. Swallowing `onClick` is not enough on its own: a
   * `type="submit"` button still submits its form on Enter without ever firing a click handler,
   * so an aria-disabled submit would stay fully operable by keyboard — the exact users this
   * prop exists to serve.
   */
  ariaDisabled?: boolean;
  /**
   * Why the button is unavailable. Only used while {@link disabled} is set: the button keeps its
   * disabled look, shows this text in a tooltip on hover and keyboard focus, and points
   * `aria-describedby` at it.
   *
   * To make that possible the button is rendered the {@link ariaDisabled} way (focusable, click
   * swallowed, forced `type="button"`) instead of with the native `disabled` attribute, which
   * blocks both hover and focus. Replaces {@link tooltip} while shown.
   */
  disabledReason?: string;
  /**
   * Cut a long label with an ellipsis instead of letting the button grow. On by default, as it always
   * was. Pass `false` to keep the label on one line at its full width (it never wraps either way).
   * Pair a truncated label with a {@link tooltip} holding the full text.
   */
  truncate?: boolean;
}

// Fragments flattened, so `<>Send <b>3</b></>` is judged by what it holds.
const flatChildren = (children: React.ReactNode): React.ReactNode[] =>
  React.Children.toArray(children).flatMap((child) =>
    React.isValidElement<{ children?: React.ReactNode }>(child) && child.type === React.Fragment
      ? flatChildren(child.props.children)
      : [child]
  );

// An icon passed beside its label sits in the label span, where the reset's `svg { display: block }`
// would stack it above the text. Such labels lay out as a row instead. A lone component child (a
// `<Trans>` label) keeps the plain truncating span.
const hasInlineIcon = (children: React.ReactNode) => {
  const parts = flatChildren(children);
  return parts.length > 1 && parts.some((child) => React.isValidElement(child) && typeof child.type !== "string");
};

// Runs of text become one span, so "Send {count} RFPs" stays a single truncatable label.
const groupText = (children: React.ReactNode, textClass: string) => {
  const parts: React.ReactNode[] = [];
  let text: React.ReactNode[] = [];
  const flush = () => {
    if (text.length)
      parts.push(
        <span key={`text-${parts.length}`} className={textClass}>
          {text}
        </span>
      );
    text = [];
  };
  for (const child of flatChildren(children)) {
    if (typeof child === "string" || typeof child === "number") text.push(child);
    else {
      flush();
      // Re-keyed: children lifted out of different fragments can share a key.
      parts.push(React.isValidElement(child) ? React.cloneElement(child, { key: `node-${parts.length}` }) : child);
    }
  }
  flush();
  return parts;
};

/**
 * Nowadays themed button.
 *
 * The label never wraps; it ends in an ellipsis when the button is too narrow (`truncate={false}` lets it overflow instead). For an icon-only button use
 * `FetchIconButton`, which requires a label for its tooltip and accessible name.
 *
 * @param props - See {@link FetchButtonProps}.
 * @returns The button, or a link when `href` is set, wrapped in a tooltip when one is given.
 */
export const FetchButton = ({
  children,
  variant = "primary",
  color = "primary",
  size = "md",
  padding,
  leftIcon,
  rightIcon,
  iconOnly = false,
  arrow = false,
  onClick,
  className = "",
  disabled = false,
  loading = false,
  type = "button",
  href,
  download = false,
  target,
  rel,
  tooltip,
  tooltipSide = "top",
  tooltipAlign = "center",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  ariaPressed,
  ariaExpanded,
  ariaControls,
  tabIndex,
  ariaDisabled = false,
  disabledReason,
  truncate = true,
}: FetchButtonProps) => {
  const enterpriseTheme = useEnterpriseTheme();
  const disabledReasonId = useId();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // A disabled button with a reason has to stay hoverable and focusable for the reason to be
  // read, so it takes the `ariaDisabled` path instead of the native `disabled` attribute.
  const hasDisabledReason = disabled && Boolean(disabledReason);
  const isAriaDisabled = ariaDisabled || hasDisabledReason;
  const isNativeDisabled = (disabled && !hasDisabledReason) || loading;
  const isText = variant === "text";
  const describedBy =
    [ariaDescribedby, hasDisabledReason ? disabledReasonId : undefined].filter(Boolean).join(" ") || undefined;

  useEffect(() => {
    if (buttonRef.current && !loading) {
      const width = buttonRef.current.offsetWidth;
      const height = buttonRef.current.offsetHeight;
      // The JSX deps re-run this on every parent render, so skip the call when the size is the same.
      // Even a same-value updater can cost a render (React cannot always drop it early), and each
      // extra commit per keystroke counts toward React 19's update-depth limit (#185).
      if (width !== dimensions.width || height !== dimensions.height) setDimensions({ width, height });
    }
  }, [children, size, iconOnly, leftIcon, rightIcon, arrow, loading, dimensions.width, dimensions.height]);

  // Size configurations
  const sizeConfig = {
    xs: {
      textClass: "text-tag-sm",
      paddingClass: iconOnly ? "p-1.5" : "px-2.5 py-1.5",
      paddingClassWithRightIcon: iconOnly ? "p-1.5" : "px-2.5 py-1.5 pr-1",
      paddingClassWithLeftIcon: iconOnly ? "p-1.5" : "px-2.5 py-1.5 pl-1",
      iconSize: "w-4 h-4",
    },
    sm: {
      textClass: "text-tag-sm",
      paddingClass: iconOnly ? "p-2" : "px-3 py-2",
      paddingClassWithRightIcon: iconOnly ? "p-2" : "px-3 py-2 pr-1.5",
      paddingClassWithLeftIcon: iconOnly ? "p-2" : "px-3 py-2 pl-1.5",
      iconSize: "w-5 h-5",
    },
    md: {
      textClass: "text-tag-md",
      paddingClass: iconOnly ? "p-3" : "px-4 py-3",
      paddingClassWithRightIcon: iconOnly ? "p-3" : "px-4 py-3 pr-2",
      paddingClassWithLeftIcon: iconOnly ? "p-3" : "px-4 py-3 pl-2",
      iconSize: "w-5 h-5",
    },
    lg: {
      textClass: "text-tag-md",
      paddingClass: iconOnly ? "p-3" : "px-5 py-3.5",
      paddingClassWithRightIcon: iconOnly ? "p-3" : "px-5 py-3.5 pr-2.5",
      paddingClassWithLeftIcon: iconOnly ? "p-3" : "px-5 py-3.5 pl-2.5",
      iconSize: "w-5 h-5",
    },
    xl: {
      textClass: "text-tag-lg",
      paddingClass: iconOnly ? "p-4" : "px-6 py-4",
      paddingClassWithRightIcon: iconOnly ? "p-4" : "px-6 py-4 pr-3",
      paddingClassWithLeftIcon: iconOnly ? "p-4" : "px-6 py-4 pl-3",
      iconSize: "w-7 h-7",
    },
  };

  const currentSize = sizeConfig[size];
  const currentPadding = padding ? sizeConfig[padding] : currentSize;

  // Determine padding class based on icons
  const getPaddingClass = () => {
    if (iconOnly) return currentPadding.paddingClass;
    if (leftIcon && !rightIcon && !arrow) return currentPadding.paddingClassWithLeftIcon;
    if ((rightIcon || arrow) && !leftIcon) return currentPadding.paddingClassWithRightIcon;
    return currentPadding.paddingClass;
  };

  // `focus:ring-0` clears the default (always-on) ring so it doesn't show on a mouse click;
  // `focus-visible:ring-2!` puts a visible ring back for keyboard focus specifically; it is important
  // because the inline `boxShadow` style (the 3D inset) would otherwise beat the ring's box-shadow.
  // `text` swaps the padded, clipped box for tight padding plus a hit-area pseudo-element, which
  // `overflow-hidden` would clip. Every other variant keeps its exact classes.
  const shapeClasses = isText ? TEXT_BUTTON_CLASS : `${getPaddingClass()} rounded-xl font-medium overflow-hidden`;
  const baseClasses = `landing-text ${currentSize.textClass} ${shapeClasses} inline-flex touch-manipulation justify-center items-center gap-2 cursor-pointer transition-all duration-300 border focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2! focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed active:scale-95 select-none relative`;

  // Color theme configurations (static colors, no interpolation)
  const getColorStyles = (colorTheme: string) => {
    switch (colorTheme) {
      // "primary" and the fallback both resolve to the current enterprise theme's colors,
      // so they share one body instead of two copies of the same object shape.
      case "primary":
      default: {
        const { primaryColor, primaryHoverColor, primarySecondaryBg, primarySecondaryHoverBg, indigoColors } =
          enterpriseTheme;
        const primaryRgb = hexToRgb(primaryColor) ?? DEFAULT_PRIMARY_RGB;
        const secondaryTextColor = indigoColors ? indigoColors[900] : "#27253b";

        return {
          solid: {
            bg: primaryColor,
            text: "#ffffff",
            border: primaryColor,
            hoverBg: primaryHoverColor,
            hoverText: "#ffffff",
          },
          secondary: {
            bg: primarySecondaryBg,
            text: secondaryTextColor,
            border: "transparent",
            hoverBg: primarySecondaryHoverBg,
            hoverText: secondaryTextColor,
          },
          outline: {
            bg: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.05)`,
            text: primaryColor,
            border: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`,
            hoverBg: primaryColor,
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: primaryColor,
            border: "transparent",
            hoverBg: `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`,
            hoverText: primaryColor,
          },
        };
      }

      case "neutral":
        return {
          solid: {
            bg: "#2D3748",
            text: "#ffffff",
            border: "#2D3748",
            hoverBg: "#1A202C",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#F7FAFC",
            text: "#4A5568",
            border: "transparent",
            hoverBg: "#EDF2F7",
            hoverText: "#4A5568",
          },
          outline: {
            bg: "rgba(74, 85, 104, 0.05)",
            text: "#4A5568",
            border: "rgba(203, 213, 224, 0.6)",
            hoverBg: "#F7FAFC",
            hoverText: "#4A5568",
          },
          ghost: {
            bg: "transparent",
            text: "#4A5568",
            border: "transparent",
            hoverBg: "rgba(74, 85, 104, 0.1)",
            hoverText: "#4A5568",
          },
        };

      case "destructive":
        return {
          solid: {
            bg: "#ef4444",
            text: "#ffffff",
            border: "#ef4444",
            hoverBg: "#dc2626",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#fee2e2",
            text: "#dc2626",
            border: "transparent",
            hoverBg: "#fecaca",
            hoverText: "#dc2626",
          },
          outline: {
            bg: "rgba(239, 68, 68, 0.05)",
            text: "#ef4444",
            border: "rgba(239, 68, 68, 0.3)",
            hoverBg: "#ef4444",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#ef4444",
            border: "transparent",
            hoverBg: "rgba(239, 68, 68, 0.1)",
            hoverText: "#ef4444",
          },
        };

      case "success":
        return {
          solid: {
            bg: "#10b981",
            text: "#ffffff",
            border: "#10b981",
            hoverBg: "#059669",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#d1fae5",
            text: "#065f46",
            border: "transparent",
            hoverBg: "#a7f3d0",
            hoverText: "#065f46",
          },
          outline: {
            bg: "rgba(16, 185, 129, 0.05)",
            text: "#10b981",
            border: "rgba(16, 185, 129, 0.3)",
            hoverBg: "#10b981",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#10b981",
            border: "transparent",
            hoverBg: "rgba(16, 185, 129, 0.1)",
            hoverText: "#10b981",
          },
        };

      case "warning":
        return {
          // amber-700/800, not the amber-500/600 the rest of this token's shades use: white text
          // on amber-500 (#f59e0b) is ~2.15:1, and amber-600 (#d97706) hover is ~3.18:1 — both
          // fail the WCAG AA 4.5:1 minimum for button text. amber-700 clears it at ~5:1.
          solid: {
            bg: "#b45309",
            text: "#ffffff",
            border: "#b45309",
            hoverBg: "#92400e",
            hoverText: "#ffffff",
          },
          secondary: {
            bg: "#fef3c7",
            text: "#92400e",
            border: "transparent",
            hoverBg: "#fde68a",
            hoverText: "#92400e",
          },
          outline: {
            bg: "rgba(245, 158, 11, 0.05)",
            text: "#f59e0b",
            border: "rgba(245, 158, 11, 0.3)",
            hoverBg: "#f59e0b",
            hoverText: "#ffffff",
          },
          ghost: {
            bg: "transparent",
            text: "#f59e0b",
            border: "transparent",
            hoverBg: "rgba(245, 158, 11, 0.1)",
            hoverText: "#f59e0b",
          },
        };
    }
  };

  // Variant configurations
  const getVariantStyles = () => {
    const colorStyles = getColorStyles(color);

    switch (variant) {
      case "primary":
        return {
          backgroundColor: colorStyles.solid.bg,
          color: colorStyles.solid.text,
          borderColor: colorStyles.solid.border,
          hoverBg: colorStyles.solid.hoverBg,
          hoverColor: colorStyles.solid.hoverText,
        };

      case "secondary":
        return {
          backgroundColor: colorStyles.secondary.bg,
          color: colorStyles.secondary.text,
          borderColor: colorStyles.secondary.border,
          hoverBg: colorStyles.secondary.hoverBg,
          hoverColor: colorStyles.secondary.hoverText,
        };

      case "outline":
        return {
          backgroundColor: colorStyles.outline.bg,
          color: colorStyles.outline.text,
          borderColor: colorStyles.outline.border,
          hoverBg: colorStyles.outline.hoverBg,
          hoverColor: colorStyles.outline.hoverText,
        };

      case "ghost":
        return {
          backgroundColor: colorStyles.ghost.bg,
          color: colorStyles.ghost.text,
          borderColor: colorStyles.ghost.border,
          hoverBg: colorStyles.ghost.hoverBg,
          hoverColor: colorStyles.ghost.hoverText,
        };

      case "text": {
        // Text color only: no background or border at rest or on hover.
        const textColors =
          color === "primary"
            ? { text: enterpriseTheme.primaryColor, hoverText: enterpriseTheme.primaryHoverColor }
            : TEXT_BUTTON_COLORS[color];
        return {
          backgroundColor: "transparent",
          color: textColors.text,
          borderColor: "transparent",
          hoverBg: "transparent",
          hoverColor: textColors.hoverText,
        };
      }

      case "destructive":
        // Destructive variant always uses destructive color
        const destructiveStyles = getColorStyles("destructive");
        return {
          backgroundColor: destructiveStyles.solid.bg,
          color: destructiveStyles.solid.text,
          borderColor: destructiveStyles.solid.border,
          hoverBg: destructiveStyles.solid.hoverBg,
          hoverColor: destructiveStyles.solid.hoverText,
        };

      default:
        return {
          backgroundColor: colorStyles.solid.bg,
          color: colorStyles.solid.text,
          borderColor: colorStyles.solid.border,
          hoverBg: colorStyles.solid.hoverBg,
          hoverColor: colorStyles.solid.hoverText,
        };
    }
  };

  const styles = getVariantStyles();

  // Add 3D effects for primary and secondary variants
  const get3DEffects = (variant: string, color: string) => {
    if (variant === "primary" || variant === "secondary") {
      const isSecondary = variant === "secondary";

      // Adjust shadow intensity for secondary variant
      const shadowMultiplier = isSecondary ? 0.7 : 1;
      const highlightMultiplier = isSecondary ? 0.6 : 1;

      // Enhanced base shadow for better depth (no drop shadow for secondary)
      const baseShadow = isSecondary
        ? `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.25 * highlightMultiplier})`
        : `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.25 * highlightMultiplier})`;

      // Hover shadow for enhanced depth (no drop shadow for secondary)
      const hoverShadow = isSecondary
        ? `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.35 * highlightMultiplier})`
        : `inset 0 1px 2px 0 rgba(255, 255, 255, ${0.35 * highlightMultiplier})`;

      // Active shadow for pressed state
      const activeShadow = `inset 0 3px 6px 0 rgba(0, 0, 0, ${0.15 * shadowMultiplier}), inset 0 1px 2px 0 rgba(255, 255, 255, ${0.2 * highlightMultiplier})`;

      return {
        baseShadow,
        hoverShadow,
        activeShadow,
        transform: "translateY(0)",
        hoverTransform: "translateY(0)",
        activeTransform: "translateY(1px)",
      };
    }

    return {
      baseShadow: "none",
      hoverShadow: "none",
      activeShadow: "none",
      transform: "translateY(0)",
      hoverTransform: "translateY(0)",
      activeTransform: "translateY(0)",
    };
  };

  const effects = get3DEffects(variant, color);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    e.currentTarget.style.backgroundColor = styles.hoverBg;
    if (styles.hoverColor) {
      e.currentTarget.style.color = styles.hoverColor;
    }
    // Apply hover effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.hoverShadow;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    e.currentTarget.style.backgroundColor = styles.backgroundColor;
    e.currentTarget.style.color = styles.color;
    // Reset effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.baseShadow;
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    // Apply active effects for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.activeShadow;
      e.currentTarget.style.transform = effects.activeTransform;
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || ariaDisabled || !SUPPORTS_HOVER) return;
    // Reset to hover state for primary and secondary variants
    if (variant === "primary" || variant === "secondary") {
      e.currentTarget.style.boxShadow = effects.hoverShadow;
      e.currentTarget.style.transform = effects.transform;
    }
  };

  // Arrow icon component - chevron that morphs into arrow on hover
  const ArrowIcon = () => (
    <svg
      className={`${currentSize.iconSize} transition-transform duration-300 group-hover:translate-x-1`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Base chevron path */}
      <path
        className="transition-all duration-300 ease-out"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 6l7 6-7 6"
      />
      {/* Arrow line that appears on hover */}
      <path
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 12H4"
      />
    </svg>
  );

  // Play icon component
  const PlayIcon = () => (
    <svg className={currentSize.iconSize} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  // Shared className + inline style, identical for the button and the link form so an `href`
  // FetchButton is pixel-for-pixel the same as a click one.
  const sharedClassName = `${baseClasses} ${className} group`;
  const sharedStyle: React.CSSProperties = {
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    borderColor: styles.borderColor,
    outline: "none",
    boxShadow: variant === "primary" || variant === "secondary" ? effects.baseShadow : "none",
    transform: variant === "primary" || variant === "secondary" ? effects.transform : "translateY(0)",
    transition:
      variant === "primary" || variant === "secondary" ? "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)" : "all 0.3s ease",
    position: "relative",
    ...(loading &&
      dimensions.width > 0 && {
        minWidth: `${dimensions.width}px`,
        minHeight: `${dimensions.height}px`,
      }),
  };

  // The icon/label content is identical for both element types.
  const content = (
    <>
      {/* Gradient overlay for primary and secondary variants */}
      {(variant === "primary" || variant === "secondary") && (
        <div
          className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)`,
          }}
        />
      )}

      {loading ? (
        <>
          <Loader2 className={`${currentSize.iconSize} animate-spin`} aria-hidden="true" />
        </>
      ) : iconOnly ? (
        <span className={`${currentSize.iconSize} shrink-0 relative z-10 flex items-center justify-center`}>
          {leftIcon || rightIcon || children}
        </span>
      ) : (
        <>
          {leftIcon && (
            <span className={`${currentSize.iconSize} shrink-0 relative z-10 flex items-center justify-center`}>
              {leftIcon}
            </span>
          )}

          {children &&
            (hasInlineIcon(children) ? (
              <span className="flex min-w-0 items-center gap-2 relative z-10 [&>svg]:shrink-0">
                {groupText(children, truncate ? "truncate min-w-0" : "whitespace-nowrap")}
              </span>
            ) : (
              <span className={`${truncate ? "truncate min-w-0" : "whitespace-nowrap"} relative z-10`}>{children}</span>
            ))}

          {arrow && <ArrowIcon />}

          {rightIcon && (
            <span className={`${currentSize.iconSize} shrink-0 relative z-10 flex items-center justify-center`}>
              {rightIcon}
            </span>
          )}
        </>
      )}
    </>
  );

  // When `href` is set, render a real link (so it links / opens new tabs) with identical styling.
  // A disabled link has no meaning, so a disabled `href` button falls back to the <button> form.
  // An in-app path goes through `next/link` so it navigates client-side instead of reloading the
  // app; anything else (external, protocol-relative, `mailto:`, a new tab, a download) stays an `<a>`.
  const linkProps = {
    href,
    target,
    download: download || undefined,
    rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
    className: sharedClassName,
    style: sharedStyle,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onClick,
    "aria-label": ariaLabel,
    "aria-describedby": describedBy,
    tabIndex,
  };
  const button =
    href && !disabled && !ariaDisabled && !loading ? (
      isInAppPath(href, target, download) ? (
        <Link {...linkProps} href={href}>
          {content}
        </Link>
      ) : (
        <a {...linkProps}>{content}</a>
      )
    ) : (
      <button
        ref={buttonRef}
        // See `ariaDisabled`: Enter on a submit button bypasses `onClick` entirely.
        type={isAriaDisabled ? "button" : type}
        className={sharedClassName}
        style={sharedStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        // `ariaDisabled` is advisory to the browser, so the click has to be swallowed here.
        onClick={isAriaDisabled ? undefined : onClick}
        disabled={isNativeDisabled}
        aria-disabled={isAriaDisabled || undefined}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-pressed={ariaPressed}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        tabIndex={tabIndex}
      >
        {content}
      </button>
    );

  const tooltipContent = hasDisabledReason ? disabledReason : tooltip;
  const withTooltip = tooltipContent ? (
    <FetchTooltip content={tooltipContent} side={tooltipSide} align={tooltipAlign}>
      {button}
    </FetchTooltip>
  ) : (
    button
  );

  if (!hasDisabledReason) return withTooltip;

  // The description target. `hidden` keeps it out of layout and out of the reading order, and
  // `aria-describedby` still reads text from a hidden element it points at directly.
  return (
    <>
      {withTooltip}
      <span id={disabledReasonId} hidden>
        {disabledReason}
      </span>
    </>
  );
};
